import React from "react";
import { Form, FormGroup, Row, Col } from "reactstrap";
import PropTypes from "prop-types";
import { FormattedDate } from "react-intl";
import { FormattedNumber } from "../../FormattedNumber/FormattedNumber";

import { SelectFormField, FormField, CheckFormField, CustomFormInput } from "../../../utils/FormFields";
import { AccountWayOfAction, AccountBookInfo, AccountUsage } from "./types";
import CircularProggress from "../../CircularProgress/CircularProgress";
import { parseDate } from "../../../utils/dateUtils";
import { AccountMode, AccountFoundsOrigin, AccountPurpose } from "../../../utils/Helper";

function ProfileForm({
  account,
  onChange,
  userType,
  riskTypes,
  currencies,
  errors,
  disabled,
  reps,
  companyLinks,
  mode,
  checkField,
}) {
  const show = mode !== AccountMode.New;
  let showReps = reps;
  const rep = reps
    ? account.RepId > 0
      ? reps.filter((i) => parseInt(i.id, 10) === parseInt(account.RepId, 10))
      : reps
    : [];
  let advisors = rep[0] ? rep[0].advisors : undefined;
  if (userType === "Advisor" && mode !== AccountMode.New) {
    showReps = [
      {
        id: account.RepId,
        name: account.RepName,
      },
    ];
    advisors = [
      {
        id: account.MainAdvisorId,
        name: account.MainAdvisorName,
      },
    ];
  }

  return (
    <Form>
      <FormGroup tag="fieldset">
        {show && (
          <Row>
            <Col>
              <FormField
                label="Name: "
                value={account.AccountName}
                onChange={onChange}
                name="AccountName"
                error={errors.AccountName}
                disabled={disabled}
              />
            </Col>
            <Col>
              <FormField
                label="Number: "
                value={account.AccountNumber.trim()}
                onChange={onChange}
                name="AccountNumber"
                error={errors.AccountName}
                disabled={disabled}
              />
            </Col>
            <Col>
              <CustomFormInput label="Type: ">
                {account.AccountIsPhysical ? "Physical" : "Juridical"}
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Opening Date: ">
                <FormattedDate value={parseDate(account.AccountOpeningDate)} />
              </CustomFormInput>
            </Col>
          </Row>
        )}

        {!show && (
          <Row>
            <Col>
              <FormField
                label="Name: "
                value={account.AccountName}
                onChange={onChange}
                name="AccountName"
                error={errors.AccountName}
                disabled={disabled}
              />
            </Col>
            <Col>
              <FormField
                label="Number: "
                value={account.AccountNumber ? account.AccountNumber.trim() : null}
                onChange={onChange}
                name="AccountNumber"
                error={errors.AccountNumber}
                disabled={disabled}
              />
            </Col>
            <Col>
              <CheckFormField
                label="Account Is Physical?"
                initialValue={account.AccountIsPhysical}
                value={account.AccountIsPhysical}
                onChange={onChange}
                name="AccountIsPhysical"
                error={errors.AccountIsPhysical}
                disabled={disabled}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <SelectFormField
              label="Way Give Orders: "
              value={account.AccountWayGiveOrders}
              selectItems={Object.keys(AccountWayOfAction)}
              getSelectItemId={(i) => i}
              getSelectItemName={(i) => AccountWayOfAction[i]}
              name="AccountWayGiveOrders"
              onChange={onChange}
              error={errors.AccountWayGiveOrders}
              disabled={disabled}
            />
          </Col>
          <Col>
            <SelectFormField
              label="Account Usage: "
              value={account.AccountUsage ? account.AccountUsage : ""}
              selectItems={Object.keys(AccountUsage)}
              getSelectItemId={(i) => i}
              getSelectItemName={(i) => AccountUsage[i]}
              name="AccountUsage"
              onChange={onChange}
              error={errors.AccountUsage}
              disabled={disabled}
            />
          </Col>
          <Col>
            {AccountPurpose ? (
              <SelectFormField
                label="Account Purpose: "
                name="AccountProfileObjective"
                onChange={onChange}
                disabled={disabled}
                error={errors.AccountProfileObjective}
                value={account.AccountProfileObjective ? account.AccountProfileObjective : ""}
                selectItems={AccountPurpose}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.name}
              />
            ) : (
              <CircularProggress sm />
            )}
          </Col>
          <Col>
            {currencies ? (
              <SelectFormField
                label="Currency: "
                value={account.AccountCurrencyId}
                selectItems={currencies}
                getSelectItemId={(i) => i.currencyId}
                getSelectItemName={(i) => i.currencyISO}
                name="AccountCurrencyId"
                onChange={onChange}
                error={errors.AccountCurrencyId}
                disabled={disabled}
              />
            ) : (
              <CircularProggress sm />
            )}
          </Col>
          <Col>
            {show && (
              <SelectFormField
                label="Risk: "
                disabled
                value={account.AccountProfileRiskId}
                selectItems={
                  riskTypes || [
                    {
                      InvestorProfileTypeId: account.AccountProfileRiskId,
                      InvestorProfileTypeName: account.AccountProfileRisk,
                    },
                  ]
                }
                getSelectItemId={(i) => i.InvestorProfileTypeId}
                getSelectItemName={(i) => i.InvestorProfileTypeName}
                name="AccountProfileRiskId"
                onChange={onChange}
              />
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            {show && (
              <FormField
                label="Account Status: "
                value={account.AccountStatus}
                onChange={onChange}
                name="AccountStatus"
                error={errors.AccountStatus}
                disabled={disabled}
              />
            )}
          </Col>
          <Col>
            {show && (
              <FormField
                label="Account Purpose: "
                value={account.AccountPurpose}
                onChange={onChange}
                name="AccountPurpose"
                error={errors.AccountPurpose}
                disabled={disabled}
              />
            )}
          </Col>
        </Row>

        {userType != "FundManager" &&
          (showReps && advisors ? (
            <React.Fragment>
              <SelectFormField
                label="Rep: "
                name="RepId"
                value={account.RepId}
                selectItems={[{ id: "", name: "" }].concat(showReps)}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.name}
                onChange={onChange}
                error={errors.RepId}
                disabled={disabled || (userType === "Advisor" && mode !== AccountMode.New)}
              />
              <SelectFormField
                label="Main Advisor: "
                name="MainAdvisorId"
                value={account.MainAdvisorId}
                selectItems={[{ id: "", name: "" }].concat(advisors)}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.name}
                onChange={onChange}
                error={errors.MainAdvisorId}
                disabled={disabled || (userType === "Advisor" && mode !== AccountMode.New)}
              />
            </React.Fragment>
          ) : (
            <CircularProggress sm />
          ))}
        <FormField
          label="Main Advisor Commission %: "
          value={account.MainAdvisorCommissionPercentage}
          name="MainAdvisorCommissionPercentage"
          onChange={onChange}
          type="number"
          error={errors.MainAdvisorCommissionPercentage}
          disabled={disabled || userType === "Advisor"}
          dataTypeDecimalName="percentage"
        />
        {companyLinks ? (
          <SelectFormField
            label="Company Link: "
            value={account.AccountCompanyLinkId}
            name="AccountCompanyLinkId"
            onChange={onChange}
            selectItems={companyLinks}
            getSelectItemId={(i) => i.CompanyLinkId}
            getSelectItemName={(i) => i.CompanyLinkName}
            error={errors.AccountCompanyLinkId}
            disabled={disabled}
          />
        ) : (
          <CircularProggress sm />
        )}
        <CheckFormField
          label="Automatic Credit Sink: "
          initialValue={account.AccountAutomaticCreditSink}
          value={account.AccountAutomaticCreditSink}
          onChange={onChange}
          name="AccountAutomaticCreditSink"
          error={errors.AccountAutomaticCreditSink}
          disabled={checkField ? disabled : true}
        />
        <CheckFormField
          label="Automatic Credit Coupon: "
          initialValue={account.AccountAutomaticCreditCoupon}
          value={account.AccountAutomaticCreditCoupon}
          onChange={onChange}
          name="AccountAutomaticCreditCoupon"
          error={errors.AccountAutomaticCreditCoupon}
          disabled={checkField ? disabled : true}
        />
        <CheckFormField
          label="Asks Custody: "
          initialValue={account.AccountAsksCustody}
          value={account.AccountAsksCustody}
          onChange={onChange}
          name="AccountAsksCustody"
          error={errors.AccountAsksCustody}
          disabled={checkField ? disabled : true}
        />
        <CheckFormField
          label="Fiscal Residence Is Local: "
          initialValue={account.AccountFiscalResidenceIsLocal}
          disabled={checkField ? disabled : true}
          value={account.AccountFiscalResidenceIsLocal}
          onChange={onChange}
          name="AccountFiscalResidenceIsLocal"
        />
        <CheckFormField
          label="Condensed Activity: "
          initialValue={account.AccountCondensedActivity}
          value={account.AccountCondensedActivity}
          onChange={onChange}
          name="AccountCondensedActivity"
          error={errors.AccountCondensedActivity}
          disabled={checkField ? disabled : true}
        />
        <CheckFormField
          label="Account Regulated Customer: "
          initialValue={account.AccountRegulatedCustomer}
          value={account.AccountRegulatedCustomer}
          onChange={onChange}
          name="AccountRegulatedCustomer"
          error={errors.AccountRegulatedCustomer}
          disabled={checkField ? disabled : true}
        />
        <CheckFormField
          label="Account Is Qualified: "
          initialValue={account.AccountIsQualified}
          value={account.AccountIsQualified}
          onChange={onChange}
          name="AccountIsQualified"
          error={errors.AccountIsQualified}
          disabled={true}
        />
        {account.AccountIsPhysical == false && (
          <CheckFormField
            label="Account Use Markup: "
            initialValue={account.AccountUseMarkUP}
            disabled={checkField ? disabled : true}
            value={account.AccountUseMarkUP}
            onChange={onChange}
            name="AccountUseMarkUP"
          />
        )}
        {show && <CustomFormInput label="Phone: ">{account.AccountHolderPhone}</CustomFormInput>}
        <FormField
          label="Email: "
          value={account.AccountEmail}
          onChange={onChange}
          name="AccountEmail"
          error={errors.AccountEmail}
          disabled={disabled}
        />
        <SelectFormField
          label="Book Info: "
          value={account.AccountBookInfo}
          onChange={onChange}
          name="AccountBookInfo"
          error={errors.AccountBookInfo}
          disabled={disabled}
          selectItems={[""].concat(Object.keys(AccountBookInfo))}
          getSelectItemId={(i) => i}
          getSelectItemName={(i) => AccountBookInfo[i] || ""}
        />
        {AccountFoundsOrigin ? (
          <SelectFormField
            label="Founds Origin"
            name="AccountFoundsOrigin"
            onChange={onChange}
            disabled={disabled}
            error={errors.AccountFoundsOrigin}
            value={account.AccountFoundsOrigin}
            selectItems={AccountFoundsOrigin}
            getSelectItemId={(i) => i.id}
            getSelectItemName={(i) => i.name}
          />
        ) : (
          <CircularProggress sm />
        )}

        {show && <CustomFormInput label="Main Holder Address: ">{account.MainHolderAddress}</CustomFormInput>}
        {show && (
          <CustomFormInput label="Total Cash: ">
            <FormattedNumber value={account.AccountTotalCash} dataTypeDecimalName="currency" />
          </CustomFormInput>
        )}
      </FormGroup>
    </Form>
  );
}

ProfileForm.propTypes = {
  account: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  riskTypes: PropTypes.array,
  currencies: PropTypes.array,
  disabled: PropTypes.bool.isRequired,
  companyLinks: PropTypes.array,
  reps: PropTypes.array,
};

ProfileForm.defaultProps = {
  riskTypes: undefined,
  currencies: undefined,
  companyLinks: undefined,
  reps: undefined,
};

export default ProfileForm;
